// src/pages/scheduler/SchedulerPage.tsx — Info Hub Scheduler admin app.
// Route /scheduler (SSO admin). Responsive master-detail shell ported from the
// prototype scheduler-app.jsx, wired to /api/scheduler/*. Renders every trigger
// from the data contract — no per-job UI. Heather/brass admin tone.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "../../auth";
import { apiFetch, ApiError } from "../../api";
import { C, F } from "../../theme";
import * as Icons from "../../components/ui/Icon";
import { TopBar } from "../../components/ui";
import { SummaryStrip, GroupHeader, TriggerCard } from "./SchedulerList";
import { TriggerDetail, type DetailPatch } from "./SchedulerDetail";
import { BuildGuide } from "./SchedulerBuildGuide";
import { configToValues, type RunRecord, type SchedApp, type SchedTrigger } from "./types";

const FILTERS: { id: string; label: string; test?: (t: SchedTrigger) => boolean }[] = [
  { id: "all", label: "All" },
  { id: "active", label: "Active", test: (t) => t.enabled },
  { id: "issues", label: "Issues", test: (t) => t.enabled && t.lastRun?.status === "failed" },
  { id: "paused", label: "Paused", test: (t) => !t.enabled },
];

function useWide() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const f = () => setW(window.innerWidth);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  return w >= 920;
}

function FilterBar({ value, onChange, counts }: { value: string; onChange: (v: string) => void; counts: Record<string, number> }) {
  return (
    <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
      {FILTERS.map((f) => {
        const on = value === f.id;
        return (
          <button key={f.id} type="button" onClick={() => onChange(f.id)} style={{
            display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 999, cursor: "pointer",
            background: on ? "#2B2417" : C.card, color: on ? C.brassGlow : C.inkSoft,
            border: `1px solid ${on ? C.brassDk : C.hairlineLt}`, fontFamily: F.body, fontWeight: 700, fontSize: 13, letterSpacing: 0.2,
          }}>
            {f.label}
            <span style={{ fontFamily: F.mono, fontWeight: 700, fontSize: 12, color: on ? "rgba(243,220,154,0.7)" : C.inkMute }}>{counts[f.id]}</span>
          </button>
        );
      })}
    </div>
  );
}

function ListBody({ triggers, apps, filter, setFilter, selectedId, onOpen, onToggle }: {
  triggers: SchedTrigger[]; apps: SchedApp[]; filter: string; setFilter: (v: string) => void;
  selectedId: string | null; onOpen: (id: string) => void; onToggle: (id: string) => void;
}) {
  const counts = Object.fromEntries(FILTERS.map((f) => [f.id, f.test ? triggers.filter(f.test).length : triggers.length]));
  const ftest = FILTERS.find((f) => f.id === filter)?.test;
  const shown = ftest ? triggers.filter(ftest) : triggers;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <SummaryStrip triggers={triggers} />
      <FilterBar value={filter} onChange={setFilter} counts={counts} />
      {apps.map((app) => {
        const list = shown.filter((t) => t.app === app.key);
        if (list.length === 0) return null;
        return (
          <div key={app.key}>
            <GroupHeader app={app} count={list.length} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
              {list.map((t) => <TriggerCard key={t.id} trig={t} active={t.id === selectedId} onOpen={() => onOpen(t.id)} onToggle={() => onToggle(t.id)} />)}
            </div>
          </div>
        );
      })}
      {shown.length === 0 && <div style={{ padding: "40px 0", textAlign: "center", fontFamily: F.serif, fontStyle: "italic", color: C.inkMute, fontSize: 16 }}>No triggers match this filter.</div>}
    </div>
  );
}

function Empty() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 20px", textAlign: "center" }}>
      <div style={{ width: 60, height: 60, borderRadius: 18, background: C.paperWarm, border: `1px solid ${C.hairlineLt}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}><Icons.Clock s={30} c={C.inkMute} /></div>
      <div style={{ fontFamily: F.serif, fontSize: 20, fontWeight: 700, color: C.ink }}>Select a trigger</div>
      <div style={{ fontSize: 14, color: C.inkSoft, fontWeight: 600, marginTop: 4 }}>Choose a scheduled job from the left to view and tune it.</div>
    </div>
  );
}

function GuideButton({ active, onClick, wide }: { active: boolean; onClick: () => void; wide: boolean }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 8, padding: wide ? "10px 15px" : "9px 12px", borderRadius: 12, cursor: "pointer",
      background: active ? C.heatherDk : C.card, color: active ? "#FFFCEC" : C.heatherDk,
      border: `1px solid ${active ? C.heatherDk : C.hairline}`, boxShadow: C.shadowS,
      fontFamily: F.body, fontWeight: 800, fontSize: 13.5, letterSpacing: 0.2, flexShrink: 0,
    }}>
      <Icons.Code s={16} c={active ? "#FFFCEC" : C.heatherDk} />{wide ? "Build guide" : "Guide"}
    </button>
  );
}

export function SchedulerPage() {
  const navigate = useNavigate();
  const { me } = useCurrentUser();
  const wide = useWide();

  const [apps, setApps] = useState<SchedApp[]>([]);
  const [triggers, setTriggers] = useState<SchedTrigger[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState<"list" | "detail" | "guide">("list"); // mobile nav
  const [guide, setGuide] = useState(false); // desktop guide pane toggle

  const load = useCallback(async () => {
    try {
      const r = await apiFetch<{ apps: SchedApp[]; triggers: SchedTrigger[] }>("/api/scheduler/triggers");
      setApps(r.apps);
      setTriggers(r.triggers);
      setSelectedId((cur) => cur ?? (r.triggers[0]?.id ?? null));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't load the scheduler.");
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const patch = useCallback(async (id: string, p: DetailPatch) => {
    const body: Record<string, unknown> = {};
    if (p.enabled !== undefined) body.enabled = p.enabled;
    if (p.schedule !== undefined) body.schedule = p.schedule;
    if (p.config !== undefined) body.config = configToValues(p.config);
    try {
      const r = await apiFetch<{ trigger: SchedTrigger }>(`/api/scheduler/triggers/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      setTriggers((ts) => ts.map((t) => (t.id === id ? r.trigger : t)));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't save the change.");
    }
  }, []);

  const toggle = useCallback((id: string) => {
    const t = triggers.find((x) => x.id === id);
    if (t && t.managed !== "external") void patch(id, { enabled: !t.enabled });
  }, [triggers, patch]);

  const runNow = useCallback(async (id: string): Promise<RunRecord | null> => {
    const r = await apiFetch<{ lastRun: RunRecord }>(`/api/scheduler/triggers/${encodeURIComponent(id)}/run`, { method: "POST" });
    setTriggers((ts) => ts.map((t) => (t.id === id ? { ...t, lastRun: r.lastRun } : t)));
    return r.lastRun;
  }, []);

  const selected = triggers.find((t) => t.id === selectedId);
  const appOf = (key: string) => apps.find((a) => a.key === key);

  const openTrigger = (id: string) => { setSelectedId(id); setGuide(false); if (!wide) setView("detail"); };
  const openGuide = () => { if (wide) setGuide((g) => !g); else setView((v) => (v === "guide" ? "list" : "guide")); };
  const onBack = () => {
    if (!wide && view !== "list") setView("list");
    else navigate("/apps");
  };

  // Admin gate — PIN admins / non-admins never see the tile, but guard direct nav too.
  if (me && !me.isSsoAdmin) {
    return (
      <div>
        <TopBar title="Scheduler" sub="Admin" onBack={() => navigate("/apps")} />
        <div style={{ padding: 24, fontFamily: F.serif, fontStyle: "italic", color: C.inkSoft }}>
          This app is for NYMR Entra admins only.
        </div>
      </div>
    );
  }

  const header = (
    <TopBar title="Scheduler" sub="Scheduled jobs" onBack={onBack}
      right={<GuideButton active={wide ? guide : view === "guide"} onClick={openGuide} wide={wide} />} />
  );

  const errorBanner = error && (
    <div role="alert" style={{ padding: 12, borderRadius: 12, background: C.signalBg, border: "1px solid rgba(184,58,43,0.22)", fontFamily: F.body, fontSize: 13, color: C.signalDk, fontWeight: 700, marginBottom: 14 }}>
      {error}
    </div>
  );

  const loadingPane = (
    <div style={{ height: 160, borderRadius: 18, background: C.paperWarm, border: `1px solid ${C.hairlineLt}` }} aria-hidden="true" />
  );

  // ── DESKTOP: two-column master-detail ──
  if (wide) {
    return (
      <div style={{ minHeight: "100vh", background: C.paper }}>
        {header}
        <div style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 0, maxWidth: 1340, margin: "0 auto", alignItems: "start" }}>
          <div style={{ padding: "20px 18px 60px", borderRight: `1px solid ${C.hairlineLt}`, position: "sticky", top: 77, maxHeight: "calc(100vh - 77px)", overflowY: "auto" }}>
            {errorBanner}
            {!loaded ? loadingPane : (
              <ListBody triggers={triggers} apps={apps} filter={filter} setFilter={setFilter} selectedId={guide ? null : selectedId} onOpen={openTrigger} onToggle={toggle} />
            )}
          </div>
          <div style={{ padding: "22px 26px 80px", maxWidth: 760, width: "100%" }}>
            {guide ? <BuildGuide />
              : selected ? <TriggerDetail key={selected.id} trig={selected} app={appOf(selected.app)} onPatch={(p) => patch(selected.id, p)} onRunNow={() => runNow(selected.id)} />
              : loaded ? <Empty /> : loadingPane}
          </div>
        </div>
      </div>
    );
  }

  // ── MOBILE: single column, push navigation ──
  return (
    <div style={{ minHeight: "100vh", background: C.paper }}>
      {header}
      <div style={{ padding: "16px 16px 48px" }}>
        {errorBanner}
        {view === "guide" ? <BuildGuide />
          : view === "detail" && selected ? <TriggerDetail key={selected.id} trig={selected} app={appOf(selected.app)} onPatch={(p) => patch(selected.id, p)} onRunNow={() => runNow(selected.id)} />
          : !loaded ? loadingPane
          : <ListBody triggers={triggers} apps={apps} filter={filter} setFilter={setFilter} selectedId={null} onOpen={openTrigger} onToggle={toggle} />}
      </div>
    </div>
  );
}
